import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, Plus, FilePlus, Receipt } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtZAR = (n) => `R ${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const KIND_LABEL = { uniform: 'Uniform', iou: 'IOU', fuel: 'Fuel' };
const KIND_COLOR = { uniform: 'text-purple-700', iou: 'text-amber-700', fuel: 'text-blue-700' };

export default function DeductionsLedgerPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('uniform');
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const [billingMonth, setBillingMonth] = useState(today.slice(0, 7));
  const [ownerFilter, setOwnerFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');

  // Add form state (per-kind)
  const [form, setForm] = useState({
    date: today,
    employee_name: '',
    description: '',
    item: '',
    size: '',
    quantity: 1,
    unit_amount: '',
    amount: '',
    owner_name: '',
  });

  const resetForm = () => setForm({
    date: today, employee_name: '', description: '', item: '', size: '',
    quantity: 1, unit_amount: '', amount: '', owner_name: '',
  });

  const fetchEntries = useCallback(async () => {
    try {
      const params = { kind: tab };
      if (billingMonth) params.billing_month = billingMonth;
      if (ownerFilter) params.owner = ownerFilter;
      if (employeeFilter) params.employee = employeeFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API_URL}/deductions-ledger/entries`, { headers, params });
      setEntries(res.data?.entries || []);
      setCount(res.data?.count || 0);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('fetchEntries failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, billingMonth, ownerFilter, employeeFilter, statusFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/deductions-ledger/summary`, {
        headers, params: { billing_month: billingMonth },
      });
      setSummary(res.data || null);
    } catch (err) {
      console.error('fetchSummary failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingMonth]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleAdd = async () => {
    const employee = (form.employee_name || '').trim();
    const amount = parseFloat(form.amount);
    if (!employee) { toast.error('Employee name is required'); return; }
    if (!amount || amount <= 0) { toast.error('Amount must be > 0'); return; }
    const payload = {
      kind: tab,
      date: form.date,
      employee_name: employee,
      amount,
      owner_name: form.owner_name?.trim() || undefined,
    };
    if (tab === 'uniform') {
      payload.item = form.item?.trim();
      payload.size = form.size?.trim();
      payload.quantity = parseInt(form.quantity, 10) || 1;
      payload.unit_amount = parseFloat(form.unit_amount) || amount;
    } else {
      payload.description = (form.description || (tab === 'iou' ? 'Loan' : 'Fuel')).trim();
    }
    try {
      await axios.post(`${API_URL}/deductions-ledger/entries`, payload, { headers });
      toast.success(`${KIND_LABEL[tab]} entry added — owner auto-resolved`);
      resetForm();
      await Promise.all([fetchEntries(), fetchSummary()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/deductions-ledger/entries/${id}`, { headers });
      toast.success('Deleted');
      await Promise.all([fetchEntries(), fetchSummary()]);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Smooth amount input: allow free typing, comma OR dot
  const onAmountChange = (key) => (e) => {
    const raw = (e.target.value || '').replace(/[^\d.,]/g, '').replace(',', '.');
    const parts = raw.split('.');
    const cleaned = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : raw;
    setForm(f => ({ ...f, [key]: cleaned }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="h-6 w-6" /> Deductions Ledger</h1>
            <p className="text-sm text-muted-foreground">Capture Uniform / IOU / Fuel debits daily. Auto-linked to the employee's owner. Pull into a statement at month-end.</p>
          </div>
        </div>

        {/* Summary strip */}
        {summary && (
          <Card className="border-violet-200 bg-violet-50/40">
            <CardContent className="py-3 flex flex-wrap items-center gap-4 text-sm">
              <strong className="text-violet-900">Open totals for {summary.billing_month}:</strong>
              <span className={KIND_COLOR.uniform}>Uniform <strong>{fmtZAR(summary.grand_total.uniform)}</strong></span>
              <span className={KIND_COLOR.iou}>IOU <strong>{fmtZAR(summary.grand_total.iou)}</strong></span>
              <span className={KIND_COLOR.fuel}>Fuel <strong>{fmtZAR(summary.grand_total.fuel)}</strong></span>
              <span className="ml-auto font-bold">Total <span className="text-violet-900">{fmtZAR(summary.grand_total.total)}</span></span>
              <span className="text-muted-foreground">({summary.grand_total.count} entries)</span>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="py-3 grid grid-cols-1 md:grid-cols-[180px_1fr_1fr_160px_auto] gap-2">
            <Input type="month" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)} data-testid="ledger-filter-month" />
            <Input value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} placeholder="Filter by OWNER..." data-testid="ledger-filter-owner" />
            <Input value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} placeholder="Filter by employee name..." data-testid="ledger-filter-employee" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="ledger-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open only</SelectItem>
                <SelectItem value="applied">Applied</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setOwnerFilter(''); setEmployeeFilter(''); setStatusFilter('open'); }} data-testid="ledger-clear-filters">Clear</Button>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); resetForm(); }}>
          <TabsList>
            <TabsTrigger value="uniform" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="ledger-tab-uniform">Uniform</TabsTrigger>
            <TabsTrigger value="iou" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="ledger-tab-iou">IOU</TabsTrigger>
            <TabsTrigger value="fuel" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="ledger-tab-fuel">Fuel</TabsTrigger>
          </TabsList>

          {['uniform', 'iou', 'fuel'].map((kind) => (
            <TabsContent key={kind} value={kind} className="space-y-3 pt-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-base flex items-center gap-2 ${KIND_COLOR[kind]}`}>
                    <FilePlus className="h-4 w-4" /> Add {KIND_LABEL[kind]} entry
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {kind === 'uniform' ? (
                    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_100px_80px_120px_120px_auto] gap-2">
                      <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="ledger-uniform-date" />
                      <Input value={form.employee_name} onChange={(e) => setForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="Name (e.g. Wayde)" data-testid="ledger-uniform-name" />
                      <Input value={form.item} onChange={(e) => setForm(f => ({ ...f, item: e.target.value }))} placeholder="Item (T-shirt)" data-testid="ledger-uniform-item" />
                      <Input value={form.size} onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))} placeholder="Size" data-testid="ledger-uniform-size" />
                      <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qty" data-testid="ledger-uniform-qty" />
                      <Input value={form.unit_amount} onChange={onAmountChange('unit_amount')} placeholder="Unit R" inputMode="decimal" className="text-right font-mono" data-testid="ledger-uniform-unit" />
                      <Input value={form.amount} onChange={onAmountChange('amount')} placeholder="Total R" inputMode="decimal" className="text-right font-mono" data-testid="ledger-uniform-amount" />
                      <Button onClick={handleAdd} className="bg-purple-700 hover:bg-purple-800" data-testid="ledger-uniform-save"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_140px_auto] gap-2">
                      <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid={`ledger-${kind}-date`} />
                      <Input value={form.employee_name} onChange={(e) => setForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="Name (e.g. Chad)" data-testid={`ledger-${kind}-name`} />
                      <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder={kind === 'iou' ? 'Description (Loan)' : 'Description (Engen N1)'} data-testid={`ledger-${kind}-desc`} />
                      <Input value={form.amount} onChange={onAmountChange('amount')} placeholder="Amount R" inputMode="decimal" className="text-right font-mono" data-testid={`ledger-${kind}-amount`} />
                      <Button onClick={handleAdd} className={kind === 'iou' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid={`ledger-${kind}-save`}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    The <b>owner</b> is auto-resolved from the employee name via the driver→owner map. You can override below if needed.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                    <Input value={form.owner_name} onChange={(e) => setForm(f => ({ ...f, owner_name: e.target.value }))} placeholder="Owner override (optional)" data-testid={`ledger-${kind}-owner`} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">{KIND_LABEL[kind]} entries — {count} rows · {fmtZAR(total)}</CardTitle>
                </CardHeader>
                <CardContent>
                  {entries.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-6">No entries match the current filters.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-28">Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Owner</TableHead>
                          {kind === 'uniform' && <TableHead>Item · Size · Qty</TableHead>}
                          {kind !== 'uniform' && <TableHead>Description</TableHead>}
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="w-24 text-center">Status</TableHead>
                          <TableHead className="w-8" />
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid={`ledger-rows-${kind}`}>
                        {entries.map((e) => (
                          <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}>
                            <TableCell className="text-xs whitespace-nowrap">{e.date}</TableCell>
                            <TableCell className="font-medium">{e.employee_name}</TableCell>
                            <TableCell className="text-violet-700">{e.owner_name}</TableCell>
                            {kind === 'uniform' ? (
                              <TableCell className="text-xs">
                                {[e.item, e.size && `(${e.size})`, e.quantity > 1 && `× ${e.quantity}`].filter(Boolean).join(' ')}
                              </TableCell>
                            ) : (
                              <TableCell className="text-xs">{e.description || '-'}</TableCell>
                            )}
                            <TableCell className="text-right font-mono font-semibold">{fmtZAR(e.amount)}</TableCell>
                            <TableCell className="text-center">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${e.status === 'open' ? 'bg-emerald-100 text-emerald-800' : e.status === 'applied' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'}`}>
                                {e.status?.toUpperCase()}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(e.id)} data-testid={`ledger-del-${e.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
